import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, X, Star, Camera, CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { trackConversion } from "@/lib/gads";

const PROPERTY_TYPES = ["appartement", "tussenwoning", "hoekwoning", "2-onder-1-kap", "vrijstaand", "bungalow", "nieuwbouw"];
const FEATURES = ["tuin", "balkon", "garage", "parkeerplaats", "berging", "zolder", "kantoor", "gelijkvloers", "zonnepanelen", "warmtepomp", "vloerverwarming", "cv-ketel"];

export default function SellerSubmit() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(user ? 1 : 0);
  const [submitting, setSubmitting] = useState(false);

  const [account, setAccount] = useState({ name: "", email: "", password: "" });
  const [property, setProperty] = useState({
    title: "", property_type: "vrijstaand", address: "", city: "", postal_code: "",
    price: "", area_m2: "", rooms: "", bedrooms: "", year_built: "",
    energy_label: "", features: [], description: "", contact_phone: "",
    photography_service_requested: false,
    physical_appointment_requested: false, preferred_appointment_dates: "",
  });
  const [photos, setPhotos] = useState([]);

  const handleFiles = (files) => {
    const list = Array.from(files || []).slice(0, 12 - photos.length);
    Promise.all(list.map((f) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res({ data_url: r.result, caption: f.name, is_primary: false });
      r.readAsDataURL(f);
    }))).then((newOnes) => {
      setPhotos((prev) => {
        const merged = [...prev, ...newOnes];
        if (!merged.some((x) => x.is_primary) && merged.length) merged[0].is_primary = true;
        return merged;
      });
    });
  };

  const removePhoto = (i) => setPhotos((prev) => {
    const nx = prev.filter((_, idx) => idx !== i);
    if (nx.length && !nx.some((x) => x.is_primary)) nx[0].is_primary = true;
    return nx;
  });
  const setPrimary = (i) => setPhotos((prev) => prev.map((p, idx) => ({ ...p, is_primary: idx === i })));
  const toggleFeature = (f) => setProperty((p) => ({ ...p, features: p.features.includes(f) ? p.features.filter((x) => x !== f) : [...p.features, f] }));

  const next = async () => {
    if (step === 0) {
      if (!account.name || !account.email || account.password.length < 6) return toast.error("Vul naam, email en een wachtwoord (min 6 tekens) in.");
      try { await register({ ...account, role: "seller" }); toast.success("Account aangemaakt"); setStep(1); }
      catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    if (step === 1) {
      const req = ["title", "address", "city", "postal_code", "price", "area_m2", "rooms", "bedrooms"];
      for (const k of req) if (!property[k]) return toast.error(`Veld verplicht: ${k}`);
    }
    setStep((s) => Math.min(s + 1, 4));
  };
  const prev = () => setStep((s) => Math.max(s - 1, user ? 1 : 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...property,
        price: parseFloat(property.price),
        area_m2: parseFloat(property.area_m2),
        rooms: parseInt(property.rooms),
        bedrooms: parseInt(property.bedrooms),
        year_built: property.year_built ? parseInt(property.year_built) : null,
        description: property.description || `${property.title} in ${property.city}.`,
        photos,
      };
      const { data } = await http.post("/seller/properties", payload);
      toast.success(data.message || "Aanvraag ontvangen");
      navigate("/verkoper?submitted=1");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const steps = ["Account", "Woning", "Foto's", "Fotografie", "Verzenden"];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">Aanmelden</p>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2">Uw woning aanmelden</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Uw aanvraag wordt beoordeeld door Woon Verder vóór publicatie. Wij nemen contact met u op voor de vervolgstappen.</p>

      {/* Stepper */}
      <div className="mt-8 flex items-center gap-2 flex-wrap">
        {steps.map((s, i) => (
          <div key={i} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${i === step ? "bg-[color:var(--wv-navy)] text-white" : i < step ? "bg-[color:var(--wv-green)]/10 text-[color:var(--wv-green-dark)]" : "bg-slate-100 text-slate-500"}`} data-testid={`step-${i}`}>
            {i + 1}. {s}
          </div>
        ))}
      </div>

      <div className="mt-8 p-6 md:p-8 bg-white border border-slate-200 rounded-2xl">
        {step === 0 && (
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="text-sm font-semibold">Naam</label><input className="wv-input mt-1" value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })} data-testid="account-name" /></div>
            <div><label className="text-sm font-semibold">Email</label><input className="wv-input mt-1" type="email" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} data-testid="account-email" /></div>
            <div><label className="text-sm font-semibold">Wachtwoord (min 6 tekens)</label><input className="wv-input mt-1" type="password" value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} data-testid="account-password" /></div>
            <div className="md:col-span-2 text-xs text-slate-500">Heeft u al een account? <Link to="/login" className="text-[color:var(--wv-navy)] font-semibold hover:underline">Log in</Link>.</div>
          </div>
        )}

        {step === 1 && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className="text-sm font-semibold">Titel</label><input className="wv-input mt-1" value={property.title} onChange={(e) => setProperty({ ...property, title: e.target.value })} placeholder="Bijv. Karakteristieke tussenwoning met tuin" data-testid="property-title" /></div>
            <div><label className="text-sm font-semibold">Type woning</label><select className="wv-input mt-1" value={property.property_type} onChange={(e) => setProperty({ ...property, property_type: e.target.value })} data-testid="property-type">{PROPERTY_TYPES.map(x => <option key={x}>{x}</option>)}</select></div>
            <div><label className="text-sm font-semibold">Vraagprijs (€)</label><input className="wv-input mt-1" type="number" value={property.price} onChange={(e) => setProperty({ ...property, price: e.target.value })} data-testid="property-price" /></div>
            <div className="md:col-span-2"><label className="text-sm font-semibold">Adres</label><input className="wv-input mt-1" value={property.address} onChange={(e) => setProperty({ ...property, address: e.target.value })} data-testid="property-address" /></div>
            <div><label className="text-sm font-semibold">Postcode</label><input className="wv-input mt-1" value={property.postal_code} onChange={(e) => setProperty({ ...property, postal_code: e.target.value })} data-testid="property-postal" /></div>
            <div><label className="text-sm font-semibold">Plaats</label><input className="wv-input mt-1" value={property.city} onChange={(e) => setProperty({ ...property, city: e.target.value })} data-testid="property-city" /></div>
            <div><label className="text-sm font-semibold">Woonoppervlak (m²)</label><input className="wv-input mt-1" type="number" value={property.area_m2} onChange={(e) => setProperty({ ...property, area_m2: e.target.value })} data-testid="property-area" /></div>
            <div><label className="text-sm font-semibold">Kamers</label><input className="wv-input mt-1" type="number" value={property.rooms} onChange={(e) => setProperty({ ...property, rooms: e.target.value })} data-testid="property-rooms" /></div>
            <div><label className="text-sm font-semibold">Slaapkamers</label><input className="wv-input mt-1" type="number" value={property.bedrooms} onChange={(e) => setProperty({ ...property, bedrooms: e.target.value })} data-testid="property-bedrooms" /></div>
            <div><label className="text-sm font-semibold">Bouwjaar</label><input className="wv-input mt-1" type="number" value={property.year_built} onChange={(e) => setProperty({ ...property, year_built: e.target.value })} data-testid="property-year" /></div>
            <div><label className="text-sm font-semibold">Energielabel</label><select className="wv-input mt-1" value={property.energy_label} onChange={(e) => setProperty({ ...property, energy_label: e.target.value })} data-testid="property-energy"><option value="">-</option>{["A+++", "A++", "A+", "A", "B", "C", "D", "E", "F", "G"].map(x => <option key={x}>{x}</option>)}</select></div>
            <div className="md:col-span-2"><label className="text-sm font-semibold">Telefoon (voor contact)</label><input className="wv-input mt-1" value={property.contact_phone} onChange={(e) => setProperty({ ...property, contact_phone: e.target.value })} data-testid="property-phone" /></div>
            <div className="md:col-span-2">
              <label className="text-sm font-semibold">Kenmerken</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {FEATURES.map((f) => (
                  <button key={f} type="button" onClick={() => toggleFeature(f)} className={`wv-chip ${property.features.includes(f) ? "active" : ""}`} data-testid={`feature-${f}`}>{f}</button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2"><label className="text-sm font-semibold">Beschrijving</label><textarea className="wv-input mt-1" rows="5" value={property.description} onChange={(e) => setProperty({ ...property, description: e.target.value })} data-testid="property-description" /></div>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="text-sm text-slate-600">Upload duidelijke foto's van uw woning. Kies de hoofdfoto (ster). Uw foto's worden pas na goedkeuring publiek.</p>
            <div className="mt-4 grid sm:grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((p, i) => (
                <div key={i} className="relative rounded-xl overflow-hidden border border-slate-200 group">
                  <img src={p.data_url} alt="" className="w-full aspect-square object-cover" />
                  <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100" data-testid={`photo-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => setPrimary(i)} className={`absolute top-1 left-1 rounded-full p-1 ${p.is_primary ? "bg-[color:var(--wv-green)] text-white" : "bg-white/90"}`} data-testid={`photo-primary-${i}`}>
                    <Star className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {photos.length < 12 && (
                <button type="button" onClick={() => fileRef.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-[color:var(--wv-navy)] hover:bg-slate-50 flex flex-col items-center justify-center gap-1 text-slate-500" data-testid="photo-upload-btn">
                  <Upload className="w-6 h-6" /><span className="text-xs font-semibold">Upload</span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(e.target.files)} />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div className="p-5 rounded-xl border-2 border-slate-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={property.physical_appointment_requested} onChange={(e) => setProperty({ ...property, physical_appointment_requested: e.target.checked })} className="mt-1" data-testid="physical-appt-toggle" />
                <div>
                  <p className="font-display font-bold text-lg">Ik wil een fysieke intake-afspraak</p>
                  <p className="text-sm text-slate-600">Woon Verder bezoekt uw woning voor persoonlijke intake, controle en advies.</p>
                </div>
              </label>
              {property.physical_appointment_requested && (
                <input className="wv-input mt-3" placeholder="Voorkeursdata / dagdelen (bv. Ma-Wo middag)" value={property.preferred_appointment_dates} onChange={(e) => setProperty({ ...property, preferred_appointment_dates: e.target.value })} data-testid="physical-appt-dates" />
              )}
            </div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 pt-2">Fotografie</p>
            <label className={`block p-5 rounded-xl border-2 cursor-pointer ${!property.photography_service_requested ? "border-[color:var(--wv-navy)] bg-[color:var(--wv-navy)]/5" : "border-slate-200"}`}>
              <input type="radio" checked={!property.photography_service_requested} onChange={() => setProperty({ ...property, photography_service_requested: false })} className="sr-only" data-testid="photo-service-none" />
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-[color:var(--wv-navy)] mt-1" />
                <div>
                  <p className="font-display font-bold text-lg">Ik lever zelf foto's aan</p>
                  <p className="text-sm text-slate-600">Gratis als onderdeel van de woningaanmelding. Woon Verder controleert kwaliteit vóór publicatie.</p>
                </div>
              </div>
            </label>
            <label className={`block p-5 rounded-xl border-2 cursor-pointer ${property.photography_service_requested ? "border-[color:var(--wv-green)] bg-[color:var(--wv-green)]/5" : "border-slate-200"}`}>
              <input type="radio" checked={property.photography_service_requested} onChange={() => setProperty({ ...property, photography_service_requested: true })} className="sr-only" data-testid="photo-service-request" />
              <div className="flex items-start gap-3">
                <Camera className="w-6 h-6 text-[color:var(--wv-green-dark)] mt-1" />
                <div>
                  <p className="font-display font-bold text-lg">Professionele fotografie via Woon Verder</p>
                  <p className="text-sm text-slate-600">Aparte betaalde service. Wij plannen een fotograaf in op afspraak. Prijs op maat.</p>
                </div>
              </div>
            </label>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <div className="p-5 rounded-xl bg-[color:var(--wv-sand)] border border-slate-200">
              <h3 className="font-display text-xl font-bold">Overzicht</h3>
              <dl className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-slate-700">
                <div><dt className="text-slate-500">Titel</dt><dd className="font-semibold">{property.title}</dd></div>
                <div><dt className="text-slate-500">Type</dt><dd className="font-semibold">{property.property_type}</dd></div>
                <div><dt className="text-slate-500">Adres</dt><dd className="font-semibold">{property.address}, {property.postal_code} {property.city}</dd></div>
                <div><dt className="text-slate-500">Vraagprijs</dt><dd className="font-semibold font-mono-num">€ {Number(property.price).toLocaleString("nl-NL")}</dd></div>
                <div><dt className="text-slate-500">Oppervlak</dt><dd className="font-semibold font-mono-num">{property.area_m2} m²</dd></div>
                <div><dt className="text-slate-500">Foto's</dt><dd className="font-semibold">{photos.length}</dd></div>
                <div><dt className="text-slate-500">Fotografie</dt><dd className="font-semibold">{property.photography_service_requested ? "Woon Verder fotografie aangevraagd" : "Eigen foto's"}</dd></div>
              </dl>
            </div>
            <div className="p-4 rounded-xl bg-[color:var(--wv-navy)]/5 border border-[color:var(--wv-navy)]/20 text-sm text-slate-700">
              <strong>Belangrijk:</strong> Uw woning wordt NIET automatisch gepubliceerd. Woon Verder beoordeelt eerst uw aanvraag. U ontvangt bericht over de vervolgstappen.
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between gap-3">
          <button onClick={prev} disabled={step === (user ? 1 : 0)} className="wv-btn-outline inline-flex items-center gap-2 disabled:opacity-40" data-testid="step-prev">
            <ArrowLeft className="w-4 h-4" /> Vorige
          </button>
          {step < 4 ? (
            <button onClick={next} className="wv-btn-primary inline-flex items-center gap-2" data-testid="step-next">
              Volgende <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={() => { trackConversion(); submit(); }} disabled={submitting} className="wv-btn-green inline-flex items-center gap-2" data-testid="submit-property-btn">
              {submitting ? "Verzenden…" : "Aanvraag verzenden"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
